'use client'

import { PlayCircle, ImageIcon } from 'lucide-react'

interface Props {
    /** Raw embed code copied from YouTube/Rutube share → embed */
    embedCode?: string
    /** Direct embed URL (fallback) */
    url?: string
    /** Image fallback when no video is provided */
    imageUrl?: string
    title?: string
    className?: string
}

/**
 * Universal video widget. Accepts:
 *  - raw <iframe ...> code from YouTube/Rutube ("Поделиться → встроить")
 *  - or a direct embed URL
 *  - or an image as fallback
 *  - or a placeholder
 */
export const VideoEmbed = ({ embedCode, url, imageUrl, title, className = '' }: Props) => {
    const wrapper = `relative aspect-video w-full overflow-hidden rounded-xl border border-border bg-muted ${className}`

    if (embedCode && /<iframe[\s\S]+<\/iframe>/i.test(embedCode)) {
        // Make the embedded iframe responsive (stretch to wrapper)
        const safe = embedCode
            .replace(/width="[^"]*"/gi, 'width="100%"')
            .replace(/height="[^"]*"/gi, 'height="100%"')
            .replace(/<iframe/gi, '<iframe style="position:absolute;inset:0;width:100%;height:100%;border:0;"')
        return <div className={wrapper} dangerouslySetInnerHTML={{ __html: safe }} />
    }

    if (url) {
        return (
            <div className={wrapper}>
                <iframe
                    src={url}
                    title={title ?? 'Видео'}
                    className="absolute inset-0 w-full h-full"
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
        )
    }

    if (imageUrl) {
        return (
            <div className={wrapper}>
                <img src={imageUrl} alt={title ?? 'Превью'} className="absolute inset-0 w-full h-full object-cover" />
            </div>
        )
    }

    return (
        <div className={`${wrapper} flex items-center justify-center`}>
            <div className="flex flex-col items-center gap-2 text-muted-foreground p-6 text-center">
                <PlayCircle className="w-12 h-12" />
                <p className="text-sm font-montserrat">Видео скоро появится</p>
            </div>
        </div>
    )
}

export default VideoEmbed
